import { useState, useEffect, useRef } from "react";
import { getFileContent, trackFileView } from "@/services/api/files";

interface UseImageLoaderOptions {
  fileId: string;
  open: boolean;
}

export function useImageLoader({ fileId, open }: UseImageLoaderOptions) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const objectUrlRef = useRef<string | null>(null);
  const viewTrackedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !fileId) return;

    let isCancelled = false;

    const fetchImage = async () => {
      setIsLoading(true);
      setImageError(false);

      try {
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = null;
        }

        const blob = await getFileContent(fileId);

        if (isCancelled) {
          URL.revokeObjectURL(URL.createObjectURL(blob));
          return;
        }

        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setImageUrl(url);

        if (viewTrackedRef.current !== fileId) {
          try {
            await trackFileView(fileId);
            viewTrackedRef.current = fileId;
          } catch (error) {
            console.error("Failed to track file view:", error);
          }
        }
      } catch (error) {
        console.error("Failed to load image:", error);
        if (!isCancelled) {
          setImageError(true);
          setImageUrl(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      isCancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [fileId, open]);

  useEffect(() => {
    if (!open) {
      viewTrackedRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  return {
    imageUrl,
    isLoading,
    imageError,
  };
}

